// src/components/Header.tsx
import React, { useState, useEffect, useRef } from 'react'; // Import useRef
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCustomerContext } from '../context/customerContext';
import { hasRole, isAuthenticated } from '../utils/auth';
import { useConfig } from '../context/ConfigContext';
import CustomerProfileModal from './CustomerProfileModal';
import { fetchCustomer } from '../utils/api';
import { Customer } from '../utils/types';
import { toast } from 'react-toastify';

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { settings, loadingSettings, errorSettings } = useConfig();
    const { user: loggedInUser } = useAuth();
    const { selectedSessionCustomer, setSelectedSessionCustomer } = useCustomerContext();

    // --- State for Profile Modal ---
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [profileCustomerData, setProfileCustomerData] = useState<Customer | null>(null);
    const [loadingProfile, setLoadingProfile] = useState(false);
    // --- State to track which button triggered loading ---
    const [loadingTrigger, setLoadingTrigger] = useState<'actingAs' | 'email' | null>(null);

    // --- Refs for the buttons ---
    const actingAsButtonRef = useRef<HTMLButtonElement>(null);
    const emailButtonRef = useRef<HTMLButtonElement>(null);


    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    // Determine if admin/staff is acting as another customer
    const isAdminOrStaff = hasRole(['super_admin', 'staff']);
    let displayCustomerName = loggedInUser?.name || 'Guest';
    let displayCustomerId = loggedInUser?.id; // Logged-in user's ID by default
    let isActingAsCustomer = false;

    if (isAdminOrStaff && selectedSessionCustomer) {
        displayCustomerName = selectedSessionCustomer.name || 'Selected Customer';
        displayCustomerId = selectedSessionCustomer.id; // Use selected customer's ID
        isActingAsCustomer = true;
    }

    const handleClearSelection = () => {
        setSelectedSessionCustomer(null);
    };

    // --- Updated Profile Modal Handler ---
    // Accept a trigger source identifier
    const handleOpenProfileModal = async (triggerSource: 'actingAs' | 'email') => {
        let customerIdToFetch: number | null = null;
        let customerNameToFetch: string | null = null;

        if (loadingProfile) return;

        // Set the trigger source
        setLoadingTrigger(triggerSource);

        if (isActingAsCustomer && selectedSessionCustomer) {
            customerIdToFetch = selectedSessionCustomer.id;
            customerNameToFetch = selectedSessionCustomer.name;
            console.log("Modal requested for 'Acting As' customer:", customerIdToFetch);
        } else if (!isActingAsCustomer && loggedInUser) {
            console.log("Modal requested for logged-in user's profile.");
            try {
                const storedCustomerId = localStorage.getItem('customer');
                // console.log("storedCustomerId if !isActingAsCustomer && loggedInUser:", storedCustomerId);

                if (storedCustomerId) {
                    const parsedId = JSON.parse(storedCustomerId).id;

                    console.log("Parsed customer ID from localStorage:", parsedId);

                    if (typeof parsedId === 'number') {
                        customerIdToFetch = parsedId;
                        customerNameToFetch = loggedInUser.name;
                    } else {
                        console.error("Invalid customer ID format in localStorage:", storedCustomerId);
                    }
                }
            } catch (e) {
                console.error("Failed to parse customer ID from localStorage:", e);
            }
        } else {
            toast.error("User or customer information missing.");
            setLoadingTrigger(null); // Reset trigger on early exit
            return;
        }

        if (!customerIdToFetch) {
            toast.error("Associated customer ID not found. Cannot load profile.");
            console.error("Customer ID missing or invalid:", customerIdToFetch);
            setLoadingTrigger(null); // Reset trigger on early exit
            return;
        }

        setLoadingProfile(true);
        setIsProfileModalOpen(true);
        setProfileCustomerData(null);

        try {
            // console.log("Fetching customer data for customer ID:", customerIdToFetch);
            const customerData = await fetchCustomer(customerIdToFetch);
            setProfileCustomerData(customerData);
        } catch (err: any) {
            console.error("Failed to load customer profile:", err);
            const nameForError = customerNameToFetch || `ID ${customerIdToFetch}`;
            if (err.response?.status === 404) {
                toast.error(`Customer profile not found for ${nameForError}.`);
            } else {
                toast.error(err.response?.data?.message || err.message || `Failed to load profile for ${nameForError}.`);
            }
            setIsProfileModalOpen(false);
        } finally {
            setLoadingProfile(false);
            setLoadingTrigger(null); // Reset trigger when loading finishes
        }
    };
    // --- End Updated Profile Modal Handler ---

    const handleCloseProfileModal = () => {
        setIsProfileModalOpen(false);
        setProfileCustomerData(null);
    };

    // --- Loading/Error states for settings ---
    if (loadingSettings) {
        return <header className="sticky top-0 z-30 bg-blue-800 text-white px-4 py-1 text-center">Loading settings...</header>;
    }
    if (errorSettings) {
        return <header className="sticky top-0 z-30 bg-red-600 text-white px-4 py-1 text-center">Error loading settings: {errorSettings}</header>;
    }

    return (
        <>
            <header className="sticky top-0 z-30 flex items-center justify-between bg-blue-800 text-white px-4 py-1">
                {/* Company Name */}
                <h1 className="text-lg md:text-2xl">
                    <Link to="/">
                        {settings?.shortname || 'Company Name'}
                    </Link>
                </h1>

                {/* Hamburger Menu Button (Mobile) */}
                <button
                    className="text-white focus:outline-none md:hidden"
                    onClick={toggleMenu}
                    aria-label="Toggle menu"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7"></path>
                    </svg>
                </button>

                {/* Navigation Links */}
                <nav className={`absolute md:static top-full left-0 right-0 text-xl md:text-sm bg-blue-200 p-5 w-full md:flex md:items-center md:w-auto md:p-0 md:bg-transparent ${isMenuOpen ? 'block' : 'hidden'}`}>
                    {isAuthenticated() ? (
                        <>
                            {/* --- Updated Acting As Info --- */}
                            {isActingAsCustomer && (
                                <div className="text-sm bg-yellow-500 text-black p-2 rounded mb-2 md:mb-0 md:mr-4 md:order-first flex items-center gap-1 flex-wrap">
                                    <span>Acting as: {displayCustomerName} (</span>
                                    {/* Make ID clickable */}
                                    <button
                                        ref={actingAsButtonRef} // Add ref
                                        onClick={() => handleOpenProfileModal('actingAs')} // Pass trigger source
                                        disabled={loadingProfile}
                                        className="font-semibold underline hover:text-blue-700 disabled:opacity-50 disabled:cursor-wait disabled:no-underline"
                                        title={`View/Edit profile for ${displayCustomerName}`}
                                    >
                                        {/* Check trigger source for loading text */}
                                        {loadingProfile && loadingTrigger === 'actingAs' ? 'Loading...' : 'Profile ID: '+displayCustomerId}
                                    </button>
                                    <span>)</span>
                                    <button onClick={handleClearSelection} className="ml-auto text-red-700 underline text-xs font-bold pl-2">(Clear)</button>
                                </div>
                            )}
                            {/* --- End Updated Acting As Info --- */}


                            {/* User Info/Role Badges */}
                            <div className="mt-4 md:mt-0 md:ml-auto flex flex-col md:flex-row md:items-center gap-2">
                                {/* --- Make Email Clickable (only if NOT acting as customer) --- */}
                                {!isActingAsCustomer && loggedInUser?.email && (
                                    <button
                                        ref={emailButtonRef} // Add ref
                                        onClick={() => handleOpenProfileModal('email')} // Pass trigger source
                                        disabled={loadingProfile}
                                        className={`text-xs md:text-sm text-blue-200 bg-blue-700 mr-2 px-3 py-1 rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-50 disabled:cursor-wait transition-colors duration-150`}
                                        title="View/Edit My Profile"
                                    >
                                        {/* Check trigger source for loading text */}
                                        {loadingProfile && loadingTrigger === 'email' ? 'Loading...' : loggedInUser.email}
                                    </button>
                                )}
                                {/* --- End Clickable Email --- */}

                                {/* Role Badges */}
                                {hasRole(['panel_user']) && <span className='text-xs md:text-sm text-blue-100 bg-blue-500 mr-2 px-3 py-1 rounded-lg'>Panel User</span>}
                                {hasRole(['staff']) && <span className='text-xs md:text-sm text-pink-100 bg-pink-600 mr-2 px-3 py-1 rounded-lg'>Staff</span>}
                                {hasRole(['dealer']) && <span className='text-xs md:text-sm text-purple-100 bg-purple-600 mr-2 px-3 py-1 rounded-lg'>Dealer</span>}
                                {hasRole(['fl']) && <span className='text-xs md:text-sm text-green-100 bg-green-600 mr-2 px-3 py-1 rounded-lg'>FL</span>}
                                {hasRole(['super_admin']) && <span className='text-xs md:text-xs text-red-100 bg-red-600 mr-2 px-3 py-1 rounded-lg'>Super Admin</span>}
                            </div>

                            {/* Standard Links */}
                            {isAdminOrStaff && (
                                <Link to="/customer-management" className="block md:inline-block mr-4 py-1 md:py-0 text-blue-600 md:text-white hover:text-gray-300" onClick={() => setIsMenuOpen(false)}>Customers Management</Link>
                            )}
                            <Link to="/logout" className="block md:inline-block mr-4 py-1 md:py-0 text-blue-600 md:text-white  hover:text-gray-300" onClick={() => setIsMenuOpen(false)}>Logout</Link>
                        </>
                    ) : (
                        <>
                            {/* Login/Register Links */}
                            <Link to="/login" className="block md:inline-block mr-4 py-1 md:py-0 hover:text-gray-300" onClick={() => setIsMenuOpen(false)}>Login</Link>
                            <Link to="/register" className="block md:inline-block mr-4 py-1 md:py-0 hover:text-gray-300" onClick={() => setIsMenuOpen(false)}>Register</Link>
                        </>
                    )}
                </nav>
            </header>

            {/* --- Render Profile Modal (No changes needed here) --- */}
            {isProfileModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
                    <div className="relative bg-gray-100 rounded-lg shadow-xl w-[80vw] max-w-4xl max-h-[800px] overflow-y-auto p-6">
                        <button
                            onClick={handleCloseProfileModal}
                            className="absolute top-2 right-3 text-gray-600 hover:text-gray-900 text-2xl font-bold z-10"
                            aria-label="Close profile modal"
                        >
                            &times;
                        </button>
                        {loadingProfile && (
                            <div className="flex justify-center items-center h-full">
                                <p className="text-gray-600">Loading profile...</p>
                            </div>
                        )}
                        {!loadingProfile && profileCustomerData && (
                            <CustomerProfileModal
                                initialCustomerData={profileCustomerData}
                            />
                        )}
                        {!loadingProfile && !profileCustomerData && (
                            <div className="flex justify-center items-center h-full text-red-500">
                                <p>Could not load customer profile data.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {/* --- End Render Profile Modal --- */}
        </>
    );
};

export default Header;
